import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Button,
  Text,
  Box,
  Divider,
  SimpleGrid,
  Badge,
  useToast
} from '@chakra-ui/react';
import { MdPictureAsPdf, MdTableChart } from 'react-icons/md';
import { MultaPolicia } from '../../services/policiaMunicipalApi';
import * as XLSX from 'xlsx';

interface ReportesMultasJuezProps {
  isOpen: boolean;
  onClose: () => void;
  multas: MultaPolicia[];
  filtros: any;
  rangoFechas: string;
}

const ReportesMultasJuez: React.FC<ReportesMultasJuezProps> = ({
  isOpen,
  onClose,
  multas,
  filtros,
  rangoFechas
}) => {
  const [generandoReporte, setGenerandoReporte] = useState(false);
  const toast = useToast();

  // Estadísticas específicas del juez municipal
  const estadisticas = {
    total: multas.length,
    porCalificar: multas.filter(m => !m.calificado).length,
    calificadas: multas.filter(m => m.calificado).length,
    pagadas: multas.filter(m => m.estatus?.toLowerCase() === 'pagado' || m.estatus?.toLowerCase() === 'pagada').length,
    pendientes: multas.filter(m => m.estatus?.toLowerCase() === 'pendiente').length,
    canceladas: multas.filter(m => m.estatus?.toLowerCase() === 'cancelado').length,
    montoTotal: multas.reduce((sum, m) => sum + (Number(m.monto) || 0), 0),
    montoPendiente: multas
      .filter(m => m.estatus?.toLowerCase() === 'pendiente')
      .reduce((sum, m) => sum + (Number(m.monto) || 0), 0),
    porcentajePagado: multas.length > 0 ? (multas.filter(m => m.estatus?.toLowerCase() === 'pagado' || m.estatus?.toLowerCase() === 'pagada').length / multas.length) * 100 : 0
  };

  const generarReportePDF = async () => {
    setGenerandoReporte(true);
    try {
      const htmlTemplate = generarPlantillaHTMLJuez();
      
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (printWindow) {
        printWindow.document.write(htmlTemplate);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        };
        
        toast({
          title: 'Reporte PDF abierto',
          description: 'El reporte se abrió en una nueva ventana para imprimir',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else {
        throw new Error('No se pudo abrir la ventana. Verifica que los popups estén habilitados.');
      }
      
      onClose();
    } catch (error) {
      toast({
        title: 'Error al generar reporte',
        description: 'No se pudo generar el reporte PDF',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGenerandoReporte(false);
    }
  };

  const generarReporteExcel = async () => {
    setGenerandoReporte(true);
    try {
      const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);
      
      XLSX.utils.sheet_add_aoa(ws, [
        ['', '', '', 'REPORTE DE MULTAS - JUZGADO MUNICIPAL'], 
        ['', '', '', 'H. Ayuntamiento de Totolac, Tlaxcala'],
        ['', '', '', `Generado el: ${fechaGeneracion}`],
        ['', '', '', rangoFechas],
        [''],
        
        ['DESGLOSE ESTADÍSTICO', '', '', ''],
        ['Total de Multas:', estadisticas.total, '', ''],
        ['Multas Por Calificar:', estadisticas.porCalificar, '', ''],
        ['Multas Calificadas:', estadisticas.calificadas, '', ''],
        ['Multas Pagadas:', estadisticas.pagadas, '', ''],
        ['Multas Pendientes:', estadisticas.pendientes, '', ''],
        ['Multas Canceladas:', estadisticas.canceladas, '', ''],
        ['Monto Total:', `$${estadisticas.montoTotal.toFixed(2)}`, '', ''],
        ['Monto Pendiente:', `$${estadisticas.montoPendiente.toFixed(2)}`, '', ''],
        ['Porcentaje Pagado:', `${estadisticas.porcentajePagado.toFixed(1)}%`, '', ''],
        [''],
        
        ['DATOS DETALLADOS', '', '', '', '', '', '', '', '', '', '', ''],
        ['Folio', 'Conductor', 'Fecha', 'Placa', 'Vehículo', 'Lugar', 'Oficial', 'Estatus', 'Calificada', 'Artículo', 'Monto', 'Observaciones']
      ], { origin: 'A1' });
      
      const datosTabla = multas.map(m => [
        m.folio || `ID-${m.id}`,
        m.ciudadano || (m.nombreConductor && m.apellidosConductor ? `${m.nombreConductor} ${m.apellidosConductor}` : 'N/A'),
        m.fechaInfraccion ? new Date(m.fechaInfraccion).toLocaleDateString('es-ES') : 'N/A',
        m.numeroPlaca || 'N/A',
        m.marca && m.modelo ? `${m.marca} ${m.modelo}` : (m.tipoVehiculo || 'N/A'),
        (m.lugarInfraccion || 'N/A').substring(0, 40),
        m.nombreOficial ? `${m.gradoOficial || ''} ${m.nombreOficial}`.trim() : (m.gradoOficial || 'N/A'),
        m.estatus || 'Pendiente',
        m.calificado ? 'Sí' : 'No',
        m.articuloViolado || 'N/A',
        Number(m.monto || 0),
        m.descripcion || 'N/A'
      ]);
      
      XLSX.utils.sheet_add_aoa(ws, datosTabla, { origin: 'A18' });
      
      ws['!cols'] = [
        { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 20 },
        { wch: 30 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 12 }, { wch: 30 }
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Reporte Multas Juez');
      
      const nombreArchivo = `Reporte_Multas_Juez_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);
      
      toast({
        title: 'Reporte Excel generado',
        description: 'El archivo se ha descargado correctamente',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
      onClose();
    } catch (error) {
      toast({
        title: 'Error al generar reporte',
        description: 'No se pudo generar el reporte Excel',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGenerandoReporte(false);
    }
  };

  const generarPlantillaHTMLJuez = (): string => {
    const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const filasTabla = multas.map(m => `
      <tr>
        <td>${m.folio || `ID-${m.id}`}</td>
        <td>${(m.ciudadano || (m.nombreConductor && m.apellidosConductor ? `${m.nombreConductor} ${m.apellidosConductor}` : 'N/A')).substring(0, 25)}</td>
        <td>${m.fechaInfraccion ? new Date(m.fechaInfraccion).toLocaleDateString('es-ES') : 'N/A'}</td>
        <td>${m.numeroPlaca || 'N/A'}</td>
        <td>${m.marca && m.modelo ? `${m.marca} ${m.modelo}` : (m.tipoVehiculo || 'N/A')}</td>
        <td>${(m.lugarInfraccion || 'N/A').substring(0, 30)}</td>
        <td>${m.nombreOficial ? `${m.gradoOficial || ''} ${m.nombreOficial}`.trim() : (m.gradoOficial || 'N/A')}</td>
        <td><span class="status-${(m.estatus || 'pendiente').toLowerCase()}">${m.estatus || 'Pendiente'}</span></td>
        <td><span class="calificado-${m.calificado ? 'si' : 'no'}">${m.calificado ? 'Sí' : 'No'}</span></td>
        <td>$${Number(m.monto || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reporte de Multas - Juzgado Municipal - ${rangoFechas}</title>
        <style>
          @media print {
            @page { margin: 1.5cm; size: A4 landscape; }
            body { margin: 0; -webkit-print-color-adjust: exact; }
            .no-print { display: none !important; }
          }
          
          * { box-sizing: border-box; }
          
          body {
            font-family: 'Arial', sans-serif;
            margin: 0; padding: 15px; background: white; color: #333;
            line-height: 1.3; font-size: 11px;
          }
          
          .container { max-width: 100%; margin: 0 auto; }
          
          .header {
            margin-bottom: 20px; padding-bottom: 15px;
            border-bottom: 3px solid #5b182b;
          }
          
          .logo-section { display: flex; align-items: center; margin-bottom: 15px; }
          .logo { width: 100px; height: 70px; margin-right: 20px; }
          .header-text { flex: 1; }
          
          .title {
            color: #5b182b; font-size: 18px; font-weight: bold;
            margin: 0 0 5px 0; text-transform: uppercase;
          }
          
          .subtitle {
            color: #666; font-size: 13px; margin: 0 0 8px 0;
          }
          
          .date-range {
            color: #bb8c52; font-size: 14px; font-weight: bold; margin: 0;
          }
          
          .data-table {
            width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 9px;
          }
          
          .data-table th, .data-table td {
            border: 1px solid #ddd; padding: 4px; text-align: left; vertical-align: top;
          }
          
          .data-table th {
            background-color: #5b182b; color: white; font-weight: bold;
            text-align: center; font-size: 8px;
          }
          
          .data-table tbody tr:nth-child(even) { background-color: #f8f9fa; }
          
          .status-pendiente { background: #5b182b; color: white; padding: 2px 6px; border-radius: 3px; font-size: 8px; }
          .status-pagado, .status-pagada { background: #bb8c52; color: white; padding: 2px 6px; border-radius: 3px; font-size: 8px; }
          .status-cancelado { background: #691f30; color: white; padding: 2px 6px; border-radius: 3px; font-size: 8px; }
          
          .calificado-si { background: #48bb78; color: white; padding: 2px 6px; border-radius: 3px; font-size: 8px; }
          .calificado-no { background: #dc2626; color: white; padding: 2px 6px; border-radius: 3px; font-size: 8px; }
          
          .statistics {
            background: #f8f9fa; padding: 15px; border-radius: 5px;
            border: 2px solid #5b182b; margin-top: 20px;
          }
          
          .stat-title {
            color: #5b182b; font-size: 14px; font-weight: bold;
            text-align: center; margin: 0 0 15px 0; text-transform: uppercase;
          }
          
          .stat-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
          
          .stat-item {
            background: white; padding: 8px; border-radius: 3px;
            border-left: 3px solid #bb8c52; text-align: center;
          }
          
          .stat-label {
            font-weight: bold; color: #333; font-size: 9px;
            margin-bottom: 3px; display: block;
          }
          
          .stat-value {
            color: #bb8c52; font-weight: bold; font-size: 14px; display: block;
          }
          
          .footer {
            text-align: center; margin-top: 20px; padding-top: 15px;
            border-top: 1px solid #dee2e6; font-size: 9px; color: #666;
          }
          
          .print-info {
            background: #e3f2fd; padding: 15px; border-radius: 5px;
            margin-bottom: 20px; text-align: center; border: 1px solid #2196f3;
          }
          
          .print-button {
            background: #5b182b; color: white; border: none; padding: 12px 25px;
            border-radius: 5px; cursor: pointer; font-size: 14px; margin: 10px; font-weight: bold;
          }
          
          .print-button:hover { background: #691f30; }
          
          @media screen {
            body { background: #f5f5f5; padding: 20px; }
            .container {
              background: white; padding: 30px; border-radius: 8px;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1); margin: 0 auto; max-width: 1200px;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="print-info no-print">
            <h3 style="margin: 0 0 10px 0; color: #1976d2;">📄 Reporte Listo para Imprimir</h3>
            <p style="margin: 0 0 15px 0;">Usa Ctrl+P o el botón de abajo para imprimir o guardar como PDF</p>
            <button class="print-button" onclick="window.print()">🖨️ Imprimir / Guardar como PDF</button>
            <button class="print-button" onclick="window.close()" style="background: #666;">❌ Cerrar</button>
          </div>
          
          <div class="header">
            <div class="logo-section">
              <img src="/img/logo.png" alt="Logo Ayuntamiento" class="logo">
              <div class="header-text">
                <h1 class="title">Reporte de Multas - Juzgado Municipal</h1>
                <h2 class="subtitle">H. Ayuntamiento de Totolac, Tlaxcala</h2>
                <h3 class="date-range">${rangoFechas}</h3>
              </div>
            </div>
          </div>
          
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 12%;">Folio</th>
                <th style="width: 18%;">Conductor</th>
                <th style="width: 10%;">Fecha</th>
                <th style="width: 10%;">Placa</th>
                <th style="width: 15%;">Vehículo</th>
                <th style="width: 20%;">Lugar</th>
                <th style="width: 15%;">Oficial</th>
                <th style="width: 8%;">Estatus</th>
                <th style="width: 8%;">Calificada</th>
                <th style="width: 10%;">Monto</th>
              </tr>
            </thead>
            <tbody>
              ${filasTabla}
            </tbody>
          </table>
          
          <div class="statistics">
            <h3 class="stat-title">Desglose Estadístico - Juzgado Municipal</h3>
            <div class="stat-grid">
              <div class="stat-item">
                <span class="stat-label">Total de Multas</span>
                <span class="stat-value">${estadisticas.total}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Por Calificar</span>
                <span class="stat-value">${estadisticas.porCalificar}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Calificadas</span>
                <span class="stat-value">${estadisticas.calificadas}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Multas Pagadas</span>
                <span class="stat-value">${estadisticas.pagadas}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Multas Pendientes</span>
                <span class="stat-value">${estadisticas.pendientes}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Multas Canceladas</span>
                <span class="stat-value">${estadisticas.canceladas}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Monto Total</span>
                <span class="stat-value">$${estadisticas.montoTotal.toFixed(2)}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Monto Pendiente</span>
                <span class="stat-value">$${estadisticas.montoPendiente.toFixed(2)}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">% Pagadas</span>
                <span class="stat-value">${estadisticas.porcentajePagado.toFixed(1)}%</span>
              </div>
            </div>
          </div>
          
          <div class="footer">
            <p><strong>Generado el:</strong> ${fechaGeneracion}</p>
            <p><strong>Sistema de Gestión de Multas - Juzgado Municipal</strong> - H. Ayuntamiento de Totolac</p>
            <p>Este documento contiene ${multas.length} registros</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#5b182b">
          Generar Reporte de Multas - Juzgado Municipal
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            {/* Información del reporte */}
            <Box>
              <Text fontWeight="bold" color="#5b182b" mb={3}>
                Información del Reporte
              </Text>
              <Box p={4} bg="gray.50" borderRadius="md">
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm">Período:</Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {rangoFechas || 'Todos los registros'}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm">Total de multas:</Text>
                    <Badge bg="#5b182b" color="white">
                      {estadisticas.total}
                    </Badge>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm">Filtros aplicados:</Text>
                    <Text fontSize="sm" color="gray.600">
                      {Object.values(filtros).filter(v => v !== '').length} activos
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </Box>

            <Divider />

            {/* Estadísticas del reporte */}
            <Box>
              <Text fontWeight="bold" color="#5b182b" mb={3}>
                Resumen Estadístico - Juzgado Municipal
              </Text>
              <SimpleGrid columns={2} spacing={4}>
                <Box p={3} bg="red.50" borderRadius="md" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="#dc2626">
                    {estadisticas.porCalificar}
                  </Text>
                  <Text fontSize="sm" color="gray.600">Por Calificar</Text>
                </Box>
                <Box p={3} bg="orange.50" borderRadius="md" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="#bb8c52">
                    {estadisticas.calificadas}
                  </Text>
                  <Text fontSize="sm" color="gray.600">Calificadas</Text>
                </Box>
                <Box p={3} bg="green.50" borderRadius="md" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="#48bb78">
                    {estadisticas.pagadas}
                  </Text>
                  <Text fontSize="sm" color="gray.600">Pagadas</Text>
                </Box>
                <Box p={3} bg="blue.50" borderRadius="md" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="#5b182b">
                    {estadisticas.pendientes}
                  </Text>
                  <Text fontSize="sm" color="gray.600">Pendientes</Text>
                </Box>
              </SimpleGrid>
              
              <Box mt={4} p={3} bg="purple.50" borderRadius="md">
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="bold">Monto Total:</Text>
                  <Text fontSize="lg" fontWeight="bold" color="#5b182b">
                    ${estadisticas.montoTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="bold">Monto Pendiente:</Text>
                  <Text fontSize="lg" fontWeight="bold" color="#bb8c52">
                    ${estadisticas.montoPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="bold">Porcentaje Pagado:</Text>
                  <Text fontSize="lg" fontWeight="bold" color="#48bb78">
                    {estadisticas.porcentajePagado.toFixed(1)}%
                  </Text>
                </HStack>
              </Box>
            </Box>

            <Divider />

            {/* Opciones de generación */}
            <Box>
              <Text fontWeight="bold" color="#5b182b" mb={3}>
                Formato del Reporte
              </Text>
              <VStack spacing={3}>
                <Button
                  leftIcon={<MdPictureAsPdf />}
                  bg="#5b182b"
                  color="white"
                  _hover={{ bg: "#691f30" }}
                  size="lg"
                  w="full"
                  onClick={generarReportePDF}
                  isLoading={generandoReporte}
                  loadingText="Generando PDF..."
                >
                  Generar Reporte PDF
                </Button>
                
                <Button
                  leftIcon={<MdTableChart />}
                  bg="#bb8c52"
                  color="white"
                  _hover={{ bg: "#a67a47" }}
                  size="lg"
                  w="full"
                  onClick={generarReporteExcel}
                  isLoading={generandoReporte}
                  loadingText="Generando Excel..."
                >
                  Generar Reporte Excel
                </Button>
              </VStack>
            </Box>

            {/* Información adicional */}
            <Box p={3} bg="yellow.50" borderRadius="md">
              <Text fontSize="sm" color="yellow.800" fontWeight="bold" mb={1}>
                Información del Reporte:
              </Text>
              <Text fontSize="xs" color="yellow.700">
                • El reporte PDF incluye análisis estadístico específico del juzgado municipal
              </Text>
              <Text fontSize="xs" color="yellow.700">
                • El reporte Excel contiene datos tabulares con información de calificación
              </Text>
              <Text fontSize="xs" color="yellow.700">
                • Los reportes respetan los filtros aplicados actualmente
              </Text>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ReportesMultasJuez;